import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/design/utils';

const alertVariants = cva(
  'relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        /*
         * **The description is `text-destructive` at FULL opacity, and the `/90` this was copied
         * with is what had to go.**
         *
         * A ninety-percent red over the DARK card composites to `#d14e47`, which is 4.12:1 against
         * `oklch(21% 0.014 255)` — under the 4.5 floor for the thirteen-pixel text this slot holds.
         * At full opacity the same colour is `#e6544b` and 4.82:1. Measured in Chromium on
         * 2026-09-12, and found by the accessibility sweep running against a stack whose analytics
         * screen had enough data to render one of these: the light theme was 5.26 either way, so
         * the softening only ever cost anything in the theme where it could not be afforded.
         *
         * What the alpha bought was a description slightly dimmer than its title. The title is
         * `font-medium` and the description is not, which is the distinction that survives a
         * contrast floor.
         */
        destructive:
          'bg-card text-destructive *:data-[slot=alert-description]:text-destructive [&>svg]:text-current',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertDescription, AlertTitle };
